import Button from "@/common/components/Button";
import CardNumberWithCopyButton from "@/common/components/CardNumberWithCopyButton";
import ImageWithDisplayName from "@/common/components/ImageWithDisplayName";
import { BillingEventData } from "@/common/types/billing/event.type";
import { formatCurrency, formatDate, formatDateTime } from "@/common/utils/formatters";
import { transformToNumber } from "@/common/utils/transformers";
import { DownloadIcon, PiggyBankIcon, UserIcon } from "lucide-react";
import React from "react";
import { twMerge } from "tailwind-merge";
import BillDetails from "../components/BillDetails";
import ConsumptionDetails from "../components/ConsumptionDetails";
import FeesDetails from "../components/FeesDetails";
import RefundDetails from "../components/RefundDetails";

export class BillingEventTransformer {
  static tableTransformer({
    fetchBillingEvent,
    disableActions,
  }: {
    fetchBillingEvent?: (id: string) => void
    disableActions?: boolean
  }) {
    return (events: BillingEventData[]) => {
      return events.map((event) => {
        const events = event.eventable;
        const data = events?.refundable ?? events?.related_eventable ?? events

        return {
          _className: event.eventable_type === 'Billing::Refund' && 'bg-danger-op',
          protocol_number: event.protocol_number,
          eventable_type: eventableTypeTranslations[event.eventable_type] || 'Nao implementado',
          details: eventableDetailsComponent[event.eventable_type] ? React.createElement(eventableDetailsComponent[event.eventable_type], { data: event.eventable }) : '',
          from_client_balance: (
            <div className="flex items-start">
              <span className="bg-neutral-op-8 p-2 rounded">
                {event.eventable?.from_client_balance ? 'Pré-pago' : 'Pós-pago'}
              </span>
            </div>
          ),
          gross_value: event.total_amount
            ? formatCurrency(
              transformToNumber({
                value: event.total_amount,
                invert: true,
              }) - transformToNumber({ value: event.fee_total, invert: true })
            )
            : "",
          total_amount: event.total_amount
            ? formatCurrency(transformToNumber({ value: event.total_amount, invert: true }))
            : "--",
          fee_total_amount: <FeesDetails {...event} />,
          income_tax_total: event.income_tax_total ? formatCurrency(parseFloat(event.income_tax_total)) : '',
          branch: event.branch_name || '',
          card: data?.card ? <CardNumberWithCopyButton card={data?.card} /> : '',
          equipment_field_for_display_value: data?.card?.equipment_field_for_display_value || '',
          buyer: data?.buyer?.full_name || '',
          created_at: formatDateTime(new Date(event?.created_at || 0)),
          refunded_at: event?.refunded_at ? formatDate(new Date(event.refunded_at)) : '--',
          licence_plate: event?.eventable?.card?.equipment_field_for_display_value,
          capture_method: event?.eventable?.capture_method?.display_name || '',
          author: event.author && (
            <ImageWithDisplayName
              placeholderIcon={UserIcon}
              imageUrl={event.author?.profile_picture?.url}
              displayName={event.author.full_name}
            />
          ),
          actions: fetchBillingEvent ? (
            <Button
              className="bg-transparent hover:bg-transparent disabled:hover:bg-transparent p-0 mx-auto"
              onClick={() => fetchBillingEvent(event.id)}
              disabled={disableActions}
              operationKlassToCheckPermission="Client::Billing::Events::Show"
            >
              <DownloadIcon strokeWidth={1} />
            </Button>
          ) : '',
        }
      });
    };
  }

  static feesDetailsTableTransformer() {
    return (fees: BillingEventData['fees']) => {
      return fees?.map((fee) => {
        const totalFee = Number(fee.amount);
        return ({
          amount: (
            <p className={twMerge(totalFee > 0 ? 'text-success' : totalFee < 0 ? 'text-danger-soft' : '')}>
              {formatCurrency(totalFee)}
            </p>
          ),
          display_name: fee?.fee_source?.fee_template?.display_name ? (
            <div className="flex gap-2 items-center">
              <PiggyBankIcon strokeWidth={0.6} className="w-5 h-5" />

              <div>
                <p>{fee.fee_source.fee_template.display_name}</p>
              </div>
            </div>
          ) : '',
          product_display_name: fee?.source?.display_name ? (
            <div className="flex gap-2 items-center">
              <img src="/Paper.svg" />

              <div>
                <p>{fee.source.display_name}</p>
              </div>
            </div>
          ) : '',
        });
      }) || [];
    };
  }

  static quickSearchModelTransformer() {
    return (events: BillingEventData[]) => {
      return events.map((event) => ({
        route: `/billing-events?fromInternalBase%5Blabel%5D=Todas&fromInternalBase%5Bvalue%5D=&search=${event.protocol_number}`,
        items: [
          ['Transação', event.protocol_number],
          ['Tipo', eventableTypeTranslations[event.eventable_type]],
          ['Detalhes', eventableDetailsQuickSearchValue[event.eventable_type] ? eventableDetailsQuickSearchValue[event.eventable_type](event) : ''],
        ],
      }));
    };
  }
}

const eventableDetailsComponent: { [key: string]: React.ComponentType<any>; } = {
  'Client::Consumption': ConsumptionDetails,
  'Billing::Refund': RefundDetails,
  'Billing::Bill': BillDetails,
  'Billing::TefTransaction': ConsumptionDetails,
};

const eventableTypeTranslations: { [key: string]: string; } = {
  'Client::Consumption': 'Consumo',
  'Billing::Refund': 'Estorno',
  'Billing::Bill': 'Fatura',
  'Billing::TefTransaction': 'Tarifa TEF',
};

export const eventableDetailsQuickSearchValue: { [key: string]: (event: BillingEventData) => string; } = {
  'Client::Consumption': (event) => `Estabelecimento: ${event?.eventable?.establishment_branch?.general_information?.fantasy_name || '--'}`,
  'Billing::Refund': (event) => `N. Protocolo: ${event?.eventable?.refundable?.protocol_number || '--'}`,
  'Billing::Bill': () => 'Tarifa de Fatura',
  'Billing::TefTransaction': (event) => `Estabelecimento: ${event?.eventable?.establishment_branch?.general_information?.fantasy_name}`,
};
